/**
 * A file being attached in the composer (assistant-ui `ComposerPrimitive.Attachments`, owner 2026-11-01): its preview
 * or kind, name and size, the upload's progress, and — when the file was refused — the reason, in words; ✕ removes it
 * (and its upload). Demo page: NextAdmin `/chats` composer (a file chip above the input).
 */
import { AttachmentPrimitive, useAuiState } from '@assistant-ui/react';
import { FileText, X } from 'lucide-react';
import { useObjectUrl } from '../../hooks/useObjectUrl.js';
import { cn } from '../../utils/cn.js';
import { formatBytes } from '../../utils/format-bytes.js';

export function ComposerAttachmentChip() {
  const attachment = useAuiState((s) => s.attachment);
  const preview = useObjectUrl(attachment.type === 'image' ? attachment.file : null);
  const status = attachment.status;
  const failed = status.type === 'incomplete';
  const progress = status.type === 'running' ? status.progress : null;

  return (
    <AttachmentPrimitive.Root
      className={cn(
        'flex w-60 items-center gap-2 rounded-lg border px-2 py-1.5 text-xs',
        failed ? 'border-danger/50 bg-danger/5' : 'border-border bg-surface',
      )}
    >
      {preview ? (
        <img src={preview} alt="" className="size-9 shrink-0 rounded object-cover" />
      ) : (
        <span className="grid size-9 shrink-0 place-items-center rounded bg-surface-alt text-muted">
          <FileText size={16} aria-hidden />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-heading">{attachment.name}</p>
        {failed ? (
          <p className="line-clamp-2 text-danger" role="alert">
            {status.message ?? 'This file could not be attached.'}
          </p>
        ) : progress !== null ? (
          <div
            role="progressbar"
            aria-label={`Uploading ${attachment.name}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className="mt-1 h-1 overflow-hidden rounded-full bg-surface-alt"
          >
            <div
              className="h-full rounded-full bg-accent transition-[width]"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        ) : (
          <p className="text-muted">{attachment.file ? formatBytes(attachment.file.size) : 'Ready'}</p>
        )}
      </div>
      <AttachmentPrimitive.Remove
        className="grid size-6 shrink-0 place-items-center rounded text-muted transition-colors hover:bg-surface-alt hover:text-heading"
        aria-label={`Remove ${attachment.name}`}
      >
        <X size={14} aria-hidden />
      </AttachmentPrimitive.Remove>
    </AttachmentPrimitive.Root>
  );
}
